/**
 * Knowledge Archive - concept relationship graph.
 *
 * Purpose (from the plan): visualize how concepts in the user's
 * mindspace relate to each other. Graph-only - no grid, no timeline.
 *
 * Data flow:
 *   useTagFilters (URL params)
 *     └→ useKnowledgeArchiveGraph(filters)         ← React Query
 *         └→ MindspaceGraph (decorated nodes)       ← decorateGraph
 *             └→ ConceptDetailSidebar (on click)
 *
 * Server-side composite top-N ranking caps results at 300 by default;
 * `total_concepts` + `capped` drive the "Showing X of Y · Show all"
 * affordance.
 *
 * The page is composition and state only. Every piece it renders lives under
 * `features/mindspace/` with a story of its own — header, cap bars, count
 * badge, the three body states, the phone header and map — so what is left
 * here is the orchestration: which branch, with which props.
 *
 * Below `md` it is a different composition, not a squeezed one: the phone
 * header (title, Graph | List, the count that is also the cap line, search
 * beside Filters) over a full-bleed graph or list. See `ArchivePhoneHeader`
 * and `ArchivePhoneMap`.
 *
 * One thing that reads as a gap and is not: there is no reachable error state.
 * `useKnowledgeArchiveGraph` catches a failed request and falls back to a
 * synthetic graph (§2.10 #5), so `error` stays undefined in every data mode.
 * `GraphErrorState` is still rendered here for the day that changes, and is
 * storied at component grain where it can actually be reached.
 */

import { type ReactNode, useMemo, useState } from 'react';
import { MindspaceLayout } from '@/components/features/mindspace/MindspaceLayout';
import { MindspaceFilterPanel } from '@/components/features/mindspace/filters/MindspaceFilterPanel';
import { MindspaceGraph } from '@/components/features/mindspace/graph/MindspaceGraph';
import { ConceptDetailSidebar } from '@/components/features/mindspace/concepts/ConceptDetailSidebar';
import { KnowledgeArchiveHeader } from '@/components/features/mindspace/KnowledgeArchiveHeader';
import {
  ArchivePhoneHeader,
  type ArchiveCount,
} from '@/components/features/mindspace/ArchivePhoneHeader';
import { ArchivePhoneMap } from '@/components/features/mindspace/ArchivePhoneMap';
import {
  GraphCapBanner,
  GraphCapOverrideBar,
} from '@/components/features/mindspace/graph/GraphCapBanner';
import {
  GraphEmptyState,
  GraphErrorState,
  GraphLoadingState,
} from '@/components/features/mindspace/graph/GraphStates';
import { useKnowledgeArchiveGraph } from '@/hooks/mindspace/useMindspace';
import { useTagFilters } from '@/hooks/mindspace/useTagFilters';
import { useArchiveView } from '@/hooks/mindspace/useArchiveView';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import { useSourceLessonOptions } from '@/hooks/mindspace/useMindspaceOptions';
import {
  decorateGraph,
  type DecoratedGraph,
} from '@/lib/mindspace/decorateGraph';
import { useActivityTracking } from '@/hooks/useActivityTracking';

/**
 * The archive fetches the endpoint's hard ceiling in one request.
 *
 * The old 300 default existed to keep the force simulation tractable. The
 * simulation is gone - nodes are pinned by `clusterLayout` - so the
 * reason went with it, and the cap actively harmed every count on the page:
 * the server ranks by `importance x recency x fsrs_attention`, so a
 * 300-of-1,247 slice over-represents struggling concepts.
 */
const NODE_LIMIT = 2000;

export function KnowledgeArchivePage() {
  // Activity tracking - time spent exploring the graph counts as study time.
  useActivityTracking({ contentType: 'knowledge_archive' });

  const compact = useMediaQuery('(max-width: 767.98px)');

  /**
   * Cluster id → lesson title, for the names painted over the clusters.
   *
   * The graph groups concepts by lesson ID so positions never move when this
   * query resolves (see `lessonKeyOf`), which leaves the titles to be looked
   * up here. `MindspaceFilterPanel` already calls this hook on the same page,
   * so it is a cache hit rather than a second request.
   */
  const lessonOptions = useSourceLessonOptions();
  const subjectLabels = useMemo(
    () => new Map((lessonOptions.data ?? []).map(l => [l.id, l.title])),
    [lessonOptions.data]
  );
  const [view, setView] = useArchiveView();
  const [selectedConceptId, setSelectedConceptId] = useState<string | null>(
    null
  );

  const filterState = useTagFilters();
  const filters = useMemo(
    () => ({
      ...filterState.asGraphFilters(),
      limit: NODE_LIMIT,
    }),
    [filterState]
  );

  const { data, isLoading, error } = useKnowledgeArchiveGraph(filters);

  const decoratedData = useMemo(() => decorateGraph(data), [data]);

  /**
   * What the OPEN concept is linked to, read off the graph the canvas draws.
   *
   * The sidebar's own `related_concepts` comes from the detail endpoint and
   * is similarity only, so the panel could never answer "what are these lines
   * attached to me". The page already holds every edge, so it answers here
   * and the panel just renders it.
   *
   * `co_occurrence` is counted rather than listed, for the same reason the
   * canvas never draws it: the backend writes one between every pair of
   * concepts in a lesson, so listing them would paste a whole lesson into the
   * panel.
   */
  const connections = useMemo(() => {
    const empty = { comesBefore: [], sameLessonCount: 0 };
    if (!selectedConceptId) return empty;
    const nameOf = new Map(decoratedData.nodes.map(n => [n.id, n.name]));
    const comesBefore: Array<{ id: string; title: string }> = [];
    let sameLessonCount = 0;
    for (const link of decoratedData.links) {
      const other =
        link.source === selectedConceptId
          ? link.target
          : link.target === selectedConceptId
            ? link.source
            : null;
      if (other == null) continue;
      if (link.kind === 'co_occurrence') sameLessonCount += 1;
      // Direction matters: a prerequisite edge points FROM what you need
      // first TO what it unlocks, so only the inbound ones come before this.
      else if (
        link.kind === 'prerequisite' &&
        link.target === selectedConceptId
      )
        comesBefore.push({ id: other, title: nameOf.get(other) ?? other });
    }
    return { comesBefore, sameLessonCount };
  }, [selectedConceptId, decoratedData]);

  /**
   * The memory state the MAP drew the selected concept in, handed to the
   * panel so the two cannot disagree. The panel used to recompute it from the
   * detail endpoint, which carries no `created_at`, and `retrievability`
   * falls back to `created_at` when a concept has never been reviewed: the
   * same concept could be amber on the map and "Learned" in the panel.
   */
  const selectedTier = useMemo(
    () => decoratedData.nodes.find(n => n.id === selectedConceptId)?.tier,
    [selectedConceptId, decoratedData]
  );

  const hasNodes = decoratedData.nodes.length > 0;

  const bodyState = { error: !!error, isLoading, hasNodes };
  const count: ArchiveCount = {
    visible: decoratedData.nodes.length,
    total: data?.total_concepts ?? null,
    capped: !!data?.capped,
    showingAll: true,
    defaultCap: NODE_LIMIT,
    onShowAll: () => {},
    onRestoreCap: () => {},
  };

  const content = compact ? (
    // No `filterPanel`: on a phone the Filters button lives in the header,
    // beside the search it narrows with, and the header owns its sheet.
    <MindspaceLayout>
      <ArchivePhoneHeader view={view} onViewChange={setView} count={count} />
      <div className='relative min-h-0 flex-1'>
        <ArchiveBody {...bodyState}>
          {data && (
            <ArchivePhoneMap
              view={view}
              data={data}
              graph={decoratedData}
              subjectLabels={subjectLabels}
              openConceptId={selectedConceptId}
              onOpenConcept={setSelectedConceptId}
            />
          )}
        </ArchiveBody>
      </div>
    </MindspaceLayout>
  ) : (
    <ArchiveDesktop
      count={count}
      bodyState={bodyState}
      graph={decoratedData}
      selectedConceptId={selectedConceptId}
      onSelectConcept={setSelectedConceptId}
      subjectLabels={subjectLabels}
      onDeselect={() => setSelectedConceptId(null)}
    />
  );

  // The drawer is a SIBLING of the layout, not one of its children: the
  // desktop layout wraps children in a card with `backdrop-blur-sm` and
  // `animate-fade-in-up` (a transform), and either makes that card the
  // containing block for `position: fixed` - so the scrim dimmed only the
  // graph card and the navbar and filter panel stayed bright. Out here its
  // `fixed inset-0` is the viewport again.
  return (
    <>
      {content}
      {selectedConceptId && (
        <ConceptDetailSidebar
          conceptId={selectedConceptId}
          modal={compact}
          comesBefore={connections.comesBefore}
          sameLessonCount={connections.sameLessonCount}
          mapTier={selectedTier}
          onClose={() => setSelectedConceptId(null)}
          onSelectConcept={setSelectedConceptId}
        />
      )}
    </>
  );
}

/** The body in whichever state the fetch is in; `children` once it has nodes. */
function ArchiveBody({
  error,
  isLoading,
  hasNodes,
  children,
}: {
  error: boolean;
  isLoading: boolean;
  hasNodes: boolean;
  children: ReactNode;
}) {
  const filterState = useTagFilters();
  if (error) {
    return <GraphErrorState onRetry={() => filterState.setFilters({})} />;
  }
  if (isLoading) return <GraphLoadingState />;
  if (!hasNodes) {
    return (
      <GraphEmptyState
        hasFilters={filterState.hasAnyFilter}
        onClear={filterState.clearAll}
      />
    );
  }
  return <>{children}</>;
}

/** The desktop composition: filter panel beside a header, cap bar and graph. */
function ArchiveDesktop({
  count,
  bodyState,
  graph,
  selectedConceptId,
  onSelectConcept,
  subjectLabels,
  onDeselect,
}: {
  count: ArchiveCount;
  bodyState: { error: boolean; isLoading: boolean; hasNodes: boolean };
  graph: DecoratedGraph;
  selectedConceptId: string | null;
  onSelectConcept: (id: string) => void;
  subjectLabels: ReadonlyMap<string, string>;
  onDeselect: () => void;
}) {
  const { visible, total, capped, showingAll } = count;
  return (
    <MindspaceLayout filterPanel={<MindspaceFilterPanel />}>
      <div className='flex h-full flex-col'>
        <KnowledgeArchiveHeader visibleCount={visible} totalCount={total} />

        {capped && !showingAll && (
          <GraphCapBanner
            visible={visible}
            total={total}
            onShowAll={count.onShowAll}
          />
        )}
        {capped && showingAll && (
          <GraphCapOverrideBar
            total={total}
            defaultCap={count.defaultCap}
            onRestoreCap={count.onRestoreCap}
          />
        )}

        <div className='relative min-h-0 flex-1'>
          <ArchiveBody {...bodyState}>
            <MindspaceGraph
              data={graph}
              selectedConceptId={selectedConceptId}
              onNodeClick={onSelectConcept}
              subjectLabels={subjectLabels}
              onBackgroundClick={onDeselect}
            />
          </ArchiveBody>
        </div>
      </div>
    </MindspaceLayout>
  );
}
